import React, { useEffect, useMemo, useState } from "react";
import "./strukturku.css";
import "./dashboard.css";
import {
  STEPS, CATEGORIES, COMPANIES, PERSONS, COMPANY_ORDER, PERSON_ORDER, ddmmyyyy,
  getGroupList, setActiveGroup, registerCustomGroup, registerGroupWithData, BuiltGroup, DEFAULT_GROUP_ID, defaultCompanyCat, suggestCategoryFromKbli, groupExists, renameGroup, deleteGroup, BUILTIN_GROUP_IDS,
} from "./data";
import {
  buildLayout, computeFlags, flagCounts, newEffCache, isUBOCandidate, effective, LayoutOpts,
  activeVersion, companyFlagLevel,
} from "./model";
import { toPercentString, greaterThanPercent, isZero } from "./rational";
import Canvas from "./Canvas";
import DetailPanel from "./DetailPanel";
import { exportChart, captureScene, savePagesPDF, exportDocChecklistPDF, exportDocChecklistCSV, exportUBOReport, exportClientSummary, DocChecklistRow, UBORow } from "./exportChart";
import { useAuth } from "./AuthContext";
import { wsApi, activityApi } from "./api";
import AdminUsers from "./AdminUsers";
import ShareDialog from "./ShareDialog";
import ExtractModal from "./ExtractModal";
import { ExtractionDoc, groupFromExtractions, mergeExtractionsIntoGroup, mergeExtractionsIntoCompany } from "./data";
import Sidebar, { PageId } from "./Sidebar";import TopHeader, { SearchHit } from "./TopHeader";
import { DashboardPage, CompaniesPage, DocumentsPage, IssuesPage, ReportsPage, SettingsPage, ChangeTimelinePage, AuditLogPage, Ctx } from "./pages";
import {
  Upload, Plus, Trash2, Share2, Download, ChevronDown, Percent, Flag as FlagIcon, Palette,
  FileText as FileTextIcon, Maximize2, Minimize2, ArrowLeft, ArrowRight, Calendar, Pencil, Users2,
  FileImage, FileStack, Layers, Network as NetworkIcon, Building2 as Building2Icon, Clock, BarChart3,
} from "lucide-react";

const LS_COLORS = "strukturku.catColors";
const LS_CAT = "strukturku.companyCat";
const LS_OPS = "strukturku.companyOps";
const LS_PRIMARY = "strukturku.primaryKbli";
const LS_DOCX = "strukturku.docExtra";
const LS_GROUPS = "strukturku.customGroups";
const LS_CURGROUP = "strukturku.currentGroup";
const LS_GROUPNAMES = "strukturku.groupNames";
const LS_HIDDEN = "strukturku.hiddenOwners";
const LS_DELETED = "strukturku.deletedGroups";
const LS_GROUPDATA = "strukturku.groupData";
const LS_SEEDED = "strukturku.seeded";
const LS_THEME = "strukturku.theme";

function loadLS<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...JSON.parse(raw) };
  } catch {
    return fallback;
  }
}
function saveLS(key: string, val: any) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* abaikan */ }
}

export default function App() {
  const { user, logout } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [showUsers, setShowUsers] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showExtract, setShowExtract] = useState(false);
  const [extractPreset, setExtractPreset] = useState<{ groupId: string; companyId: string; companyName: string } | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem(LS_THEME);
    return saved === "dark" || saved === "light" ? saved : "light";
  });
  const [page, setPage] = useState<PageId>("struktur");
  const [tab, setTab] = useState<"visualisasi" | "perusahaan" | "timeline" | "issues" | "dokumen" | "laporan">("visualisasi");
  const [sidebarMini, setSidebarMini] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [step, setStep] = useState(STEPS.length - 1);
  const [showEffective, setShowEffective] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [drawSmall, setDrawSmall] = useState(false);
  const [showOfficers, setShowOfficers] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<{ kind: "person" | "company"; id: string } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [fitSignal, setFitSignal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportKind, setExportKind] = useState<null | "png" | "pdf">(null);
  const [exportInclude, setExportInclude] = useState<Set<string>>(new Set());
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [catHighlight, setCatHighlight] = useState<string | null>(null);
  // Filter garis (tampil/sembunyi) per grup, tersimpan di browser
  const [hiddenOwnersMap, setHiddenOwnersMap] = useState<Record<string, string[]>>(() => loadLS(LS_HIDDEN, {}));

  // ---- Multi-grup klien ----
  // Struktur data lengkap tiap grup (Win Medika di-seed di sini, tersimpan di workspace/DB).
  const [groupData, setGroupData] = useState<Record<string, BuiltGroup>>(() => loadLS(LS_GROUPDATA, {}));
  const [seeded] = useState<boolean>(() => localStorage.getItem(LS_SEEDED) === "true");
  const [customGroups, setCustomGroups] = useState<{ id: string; name: string }[]>(() => {
    let list: { id: string; name: string }[] = [];
    try { list = JSON.parse(localStorage.getItem(LS_GROUPS) || "[]"); } catch { list = []; }
    list.forEach((g) => {
      const data = groupData[g.id];
      if (data) registerGroupWithData(g.id, data);
      else registerCustomGroup(g.id, g.name);
    });
    return list;
  });
  // Grup (termasuk bawaan) yang dihapus user — diterapkan sebelum validasi grup aktif
  const [deletedGroups, setDeletedGroups] = useState<string[]>(() => {
    let list: string[] = [];
    try { list = JSON.parse(localStorage.getItem(LS_DELETED) || "[]"); } catch { list = []; }
    list.forEach((id) => deleteGroup(id));
    return list;
  });
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(LS_CURGROUP);
      if (saved && groupExists(saved)) return saved;
    } catch { /* abaikan */ }
    const list = getGroupList();
    return list.length ? list[0].id : DEFAULT_GROUP_ID;
  });
  // Nama grup yang di-rename user (berlaku untuk grup bawaan & buatan), disimpan di browser
  const [groupNames, setGroupNames] = useState<Record<string, string>>(() => loadLS(LS_GROUPNAMES, {}));
  // Penanda revisi struktur grup aktif (memaksa recompute layout saat data grup berubah tanpa ganti id)
  const [structRev, setStructRev] = useState(0);
  // Terapkan override nama ke registry (idempoten, aman tiap render)
  for (const [gid, nm] of Object.entries(groupNames)) renameGroup(gid, nm);
  // Terapkan grup aktif SEBELUM perhitungan layout pada render ini
  setActiveGroup(currentGroupId);
  const groupList = getGroupList();
  // Indeks langkah yang aman terhadap grup aktif (hindari indeks basi saat ganti grup)
  const effStep = STEPS.length ? Math.min(step, STEPS.length - 1) : 0;

  useEffect(() => {
    setActiveGroup(currentGroupId);
    try { localStorage.setItem(LS_CURGROUP, currentGroupId); } catch { /* abaikan */ }
    setStep(Math.max(0, STEPS.length - 1));
    setSelected(null);
    setPinnedId(null);
    setCatHighlight(null);
    setCollapsed(new Set());
    setFlagOpen(false);
    setFitSignal((f) => f + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupId]);

  const addGroup = () => {
    const name = window.prompt("Nama grup klien baru:");
    if (!name || !name.trim()) return;
    const id = "g" + Date.now().toString(36);
    registerCustomGroup(id, name.trim());
    const next = [...customGroups, { id, name: name.trim() }];
    setCustomGroups(next);
    try { localStorage.setItem(LS_GROUPS, JSON.stringify(next)); } catch { /* abaikan */ }
    setCurrentGroupId(id);
    activityApi.log({ action: "buat_grup", target_type: "grup", target_name: name.trim(), group_id: id, group_name: name.trim() });
  };

  const applyExtractionToCompany = (docs: ExtractionDoc[], groupId: string, companyId: string) => {
    const base = groupData[groupId];
    if (!base) { setShowExtract(false); setExtractPreset(null); return; }
    const merged = mergeExtractionsIntoCompany(base, companyId, docs);
    registerGroupWithData(groupId, merged);
    setGroupData((p) => { const n = { ...p, [groupId]: merged }; saveLS(LS_GROUPDATA, n); return n; });
    setActiveGroup(groupId);
    setStructRev((r) => r + 1);
    setCurrentGroupId(groupId);
    setStep(Math.max(0, merged.steps.length - 1));
    setFitSignal((f) => f + 1);
    setShowExtract(false); setExtractPreset(null);
    const nm = merged.companies?.[companyId]?.name || extractPreset?.companyName || "PT";
    activityApi.log({ action: "susul_dokumen_ai", target_type: "perusahaan", target_name: nm, group_id: groupId, group_name: merged.name, meta: { dokumen: docs.length } });
  };

  const applyExtraction = (docs: ExtractionDoc[], name: string, targetGroupId: string | null) => {
    if (targetGroupId && groupData[targetGroupId]) {
      const merged = mergeExtractionsIntoGroup(groupData[targetGroupId], docs);
      registerGroupWithData(targetGroupId, merged);
      setGroupData((p) => { const n = { ...p, [targetGroupId]: merged }; saveLS(LS_GROUPDATA, n); return n; });
      setActiveGroup(targetGroupId);
      setStructRev((r) => r + 1);
      setCurrentGroupId(targetGroupId);
      setStep(Math.max(0, merged.steps.length - 1));
      setSelected(null); setPinnedId(null); setFitSignal((f) => f + 1);
      setShowExtract(false);
      const gname = groupList.find((g) => g.id === targetGroupId)?.name || merged.name;
      activityApi.log({ action: "tambah_pt_ai", target_type: "grup", target_name: name, group_id: targetGroupId, group_name: gname, meta: { dokumen: docs.length } });
      return;
    }
    const id = "ai" + Date.now().toString(36);
    const built = groupFromExtractions(docs, name);
    registerGroupWithData(id, built);
    const nextGroups = [...customGroups, { id, name }];
    setCustomGroups(nextGroups);
    try { localStorage.setItem(LS_GROUPS, JSON.stringify(nextGroups)); } catch { /* abaikan */ }
    setGroupData((p) => { const n = { ...p, [id]: built }; saveLS(LS_GROUPDATA, n); return n; });
    setCurrentGroupId(id);
    setShowExtract(false);
    activityApi.log({ action: "buat_grup_ai", target_type: "grup", target_name: name, group_id: id, group_name: name, meta: { dokumen: docs.length } });
  };


  const renameGroupCurrent = () => {
    const cur = groupList.find((g) => g.id === currentGroupId);
    const name = window.prompt("Ubah nama grup klien:", cur?.name || "");
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    renameGroup(currentGroupId, trimmed);
    const next = { ...groupNames, [currentGroupId]: trimmed };
    setGroupNames(next);
    saveLS(LS_GROUPNAMES, next);
    activityApi.log({ action: "ubah_nama_grup", target_type: "grup", target_name: trimmed, group_id: currentGroupId, group_name: trimmed });
  };

  const doDeleteGroup = (id: string) => {
    const delName = groupList.find((g) => g.id === id)?.name || id;
    const isBuiltin = BUILTIN_GROUP_IDS.includes(id);
    // lepas dari daftar grup buatan (bila custom)
    const nextCustom = customGroups.filter((g) => g.id !== id);
    if (nextCustom.length !== customGroups.length) {
      setCustomGroups(nextCustom);
      try { localStorage.setItem(LS_GROUPS, JSON.stringify(nextCustom)); } catch { /* abaikan */ }
    }
    // tandai terhapus (agar grup bawaan tidak muncul lagi setelah reload)
    if (isBuiltin && !deletedGroups.includes(id)) {
      const nd = [...deletedGroups, id];
      setDeletedGroups(nd);
      saveLS(LS_DELETED, nd);
    }
    deleteGroup(id);
    // pindah ke grup lain yang tersisa
    const remaining = getGroupList();
    const nextId = remaining.length ? remaining[0].id : "";
    setCurrentGroupId(nextId);
    setConfirmDelete(null);
    activityApi.log({ action: "hapus_grup", target_type: "grup", target_name: delName, group_id: id, group_name: delName });
  };

  // Filter garis per grup: derive Set dari map & sediakan setter kompatibel
  const hiddenOwners = useMemo(
    () => new Set(hiddenOwnersMap[currentGroupId] || []),
    [hiddenOwnersMap, currentGroupId]
  );
  const setHiddenOwners: React.Dispatch<React.SetStateAction<Set<string>>> = (action) => {
    setHiddenOwnersMap((m) => {
      const cur = new Set(m[currentGroupId] || []);
      const next = typeof action === "function" ? (action as (p: Set<string>) => Set<string>)(cur) : action;
      return { ...m, [currentGroupId]: [...next] };
    });
  };

  const defaultColors = useMemo(() => {
    const o: Record<string, string> = {};
    Object.keys(CATEGORIES).forEach((k) => (o[k] = CATEGORIES[k].color));
    return o;
  }, []);
  const defaultCat = useMemo(() => defaultCompanyCat(), []);
  const [catColors, setCatColors] = useState<Record<string, string>>(() => loadLS(LS_COLORS, defaultColors));
  const [companyCat, setCompanyCat] = useState<Record<string, string>>(() => loadLS(LS_CAT, defaultCat));
  const [companyOps, setCompanyOps] = useState<Record<string, string>>(() => loadLS(LS_OPS, {}));
  const [primaryKbli, setPrimaryKbli] = useState<Record<string, string>>(() => loadLS(LS_PRIMARY, {}));
  const [docExtra, setDocExtra] = useState<Record<string, Record<string, { note?: string; url?: string }>>>(() => loadLS(LS_DOCX, {}));
  const [showDocPanel, setShowDocPanel] = useState(false);
  const onDocExtra = (cid: string, dk: string, patch: { note?: string; url?: string }) =>
    setDocExtra((p) => ({ ...p, [cid]: { ...(p[cid] || {}), [dk]: { ...(p[cid]?.[dk] || {}), ...patch } } }));

  // Kategori yang sedang dipakai grup aktif (agar legenda ringkas, bukan seluruh 26 kategori)
  const usedCats = useMemo(() => {
    const s = new Set<string>();
    for (const cid of COMPANY_ORDER) s.add(companyCat[cid] ?? defaultCat[cid] ?? "none");
    if (s.size === 0) s.add("none");
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCat, defaultCat, currentGroupId, structRev]);

  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); try { localStorage.setItem(LS_THEME, theme); } catch { /* abaikan */ } }, [theme]);
  useEffect(() => { saveLS(LS_COLORS, catColors); }, [catColors]);
  useEffect(() => { saveLS(LS_CAT, companyCat); }, [companyCat]);
  useEffect(() => { saveLS(LS_OPS, companyOps); }, [companyOps]);
  useEffect(() => { saveLS(LS_PRIMARY, primaryKbli); }, [primaryKbli]);
  useEffect(() => { saveLS(LS_DOCX, docExtra); }, [docExtra]);
  useEffect(() => { saveLS(LS_HIDDEN, hiddenOwnersMap); }, [hiddenOwnersMap]);

  // Simpan seluruh workspace ke server (per-user, durable). Viewer tidak menyimpan.
  const [wsMounted, setWsMounted] = useState(false);
  useEffect(() => { setWsMounted(true); }, []);
  useEffect(() => {
    if (!wsMounted || !canEdit) return;
    const state = {
      catColors, companyCat, companyOps, primaryKbli, docExtra,
      customGroups, currentGroupId, groupNames, hiddenOwnersMap, deletedGroups,
      groupData, seeded,
    };
    const t = setTimeout(() => { wsApi.save(state).catch(() => { /* offline: tetap tersimpan di cache */ }); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catColors, companyCat, companyOps, primaryKbli, docExtra, customGroups, currentGroupId, groupNames, hiddenOwnersMap, deletedGroups, groupData, seeded, structRev, wsMounted, canEdit]);

  const opts: LayoutOpts = { collapsed, showEffective, drawSmall, showOfficers };
  const cache = useMemo(() => newEffCache(effStep), [effStep, currentGroupId, structRev]);
  const layout = useMemo(() => buildLayout(effStep, opts, cache),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effStep, collapsed, showEffective, drawSmall, showOfficers, cache, currentGroupId, structRev]);
  const flags = useMemo(() => computeFlags(effStep), [effStep, currentGroupId, structRev]);
  const counts = flagCounts(flags);
  const uboSet = useMemo(() => {
    const s = new Set<string>();
    for (const pid of PERSON_ORDER) if (isUBOCandidate(pid, cache)) s.add(pid);
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, effStep, currentGroupId, structRev]);

  const goStep = (i: number) => {
    const n = Math.max(0, Math.min(STEPS.length - 1, i));
    setStep(n);
    setFitSignal((f) => f + 1);
  };
  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
    setFitSignal((f) => f + 1);
  };
  const selectEntity = (kind: "person" | "company", id: string) => {
    setSelected({ kind, id });
    setFlagOpen(false);
    setCatHighlight(null);
  };

  const active = STEPS[effStep];
  const isEmpty = COMPANY_ORDER.length === 0;

  // Daftar pemilik/sumber yang muncul di langkah aktif (untuk legenda & dialog ekspor)
  const owners = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of layout.edges) if (!seen.has(e.from)) seen.set(e.from, e.color);
    return [...seen.entries()].map(([id, color]) => ({
      id, color, name: PERSONS[id]?.name ?? COMPANIES[id]?.short ?? id,
    }));
  }, [layout]);

  const eqSet = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every((x) => b.has(x));

  // Nama pemilik (id → nama) untuk legenda pada hasil ekspor bagan
  const ownerNames = useMemo(() => {
    const o: Record<string, string> = {};
    for (const pid of PERSON_ORDER) o[pid] = PERSONS[pid]?.name ?? pid;
    for (const cid of COMPANY_ORDER) o[cid] = COMPANIES[cid]?.short ?? cid;
    return o;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupId]);

  // Legenda kategori (hanya kategori yang dipakai grup aktif) untuk ekspor
  const categoryLegend = useMemo(() => {
    const seen = new Set<string>();
    const out: { name: string; color: string }[] = [];
    for (const cid of COMPANY_ORDER) {
      const k = companyCat[cid] ?? defaultCat[cid] ?? "none";
      if (seen.has(k) || !CATEGORIES[k]) continue;
      seen.add(k);
      out.push({ name: CATEGORIES[k].name, color: catColors[k] ?? CATEGORIES[k].color });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCat, defaultCat, catColors, currentGroupId, structRev]);

  // Ekspor hanya rantai kepemilikan yang sedang terkunci (dari kartu tertentu)
  const onExportChain = async (kind: "png" | "pdf", nodeId: string) => {
    if (exporting) return;
    const chainNodes = new Set<string>([nodeId]);
    const chainEdges = new Set<string>();
    let fr = [nodeId];
    while (fr.length) { const nx: string[] = []; for (const f of fr) for (const e of layout.edges) if (e.from === f) { chainEdges.add(e.from + "->" + e.to); if (!chainNodes.has(e.to)) { chainNodes.add(e.to); nx.push(e.to); } } fr = nx; }
    fr = [nodeId];
    while (fr.length) { const nx: string[] = []; for (const t of fr) for (const e of layout.edges) if (e.to === t) { chainEdges.add(e.from + "->" + e.to); if (!chainNodes.has(e.from)) { chainNodes.add(e.from); nx.push(e.from); } } fr = nx; }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of layout.nodes) if (chainNodes.has(n.id)) {
      minX = Math.min(minX, n.x); minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.w); maxY = Math.max(maxY, n.y + n.h + n.noteH);
    }
    if (!isFinite(minX)) return;
    setExporting(true);
    try {
      await exportChart(kind, {
        bbox: { minX, minY, maxX, maxY },
        group: groupName,
        dateLabel: active ? ddmmyyyy(active.date) : "-",
        stepLabel: `Rantai: ${ownerNames[nodeId] ?? nodeId}`,
        ownerNames, categoryLegend, chainNodes, chainEdges,
        fileSuffix: "rantai-" + nodeId,
      });
    } catch { window.alert("Gagal mengekspor rantai."); }
    finally { setExporting(false); }
  };

  // Ekspor batch: satu PDF, tiap PT jadi satu halaman (rantai kepemilikannya), + sampul & daftar isi
  const exportBatchPerPT = async () => {
    if (isEmpty || exporting) return;
    const cids = COMPANY_ORDER.filter((cid) => layout.nodeMap[cid]);
    if (!cids.length) return;
    setExporting(true);
    const pages: { dataUrl: string; w: number; h: number }[] = [];
    const labels: string[] = [];
    try {
      for (const cid of cids) {
        const chainNodes = new Set<string>([cid]);
        const chainEdges = new Set<string>();
        let fr = [cid];
        while (fr.length) { const nx: string[] = []; for (const f of fr) for (const e of layout.edges) if (e.from === f) { chainEdges.add(e.from + "->" + e.to); if (!chainNodes.has(e.to)) { chainNodes.add(e.to); nx.push(e.to); } } fr = nx; }
        fr = [cid];
        while (fr.length) { const nx: string[] = []; for (const t of fr) for (const e of layout.edges) if (e.to === t) { chainEdges.add(e.from + "->" + e.to); if (!chainNodes.has(e.from)) { chainNodes.add(e.from); nx.push(e.from); } } fr = nx; }
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const n of layout.nodes) if (chainNodes.has(n.id)) { minX = Math.min(minX, n.x); minY = Math.min(minY, n.y); maxX = Math.max(maxX, n.x + n.w); maxY = Math.max(maxY, n.y + n.h + n.noteH); }
        if (!isFinite(minX)) continue;
        const label = `Rantai: ${COMPANIES[cid]?.short ?? cid}`;
        labels.push(label);
        const page = await captureScene({
          bbox: { minX, minY, maxX, maxY }, group: groupName,
          dateLabel: active ? ddmmyyyy(active.date) : "-", stepLabel: label,
          ownerNames, categoryLegend, chainNodes, chainEdges,
        });
        pages.push(page);
      }
      await savePagesPDF(pages, groupName, labels, {
        filenameSuffix: "rantai-per-pt",
        subtitle: "Laporan Rantai Kepemilikan — Per PT",
        tocTitle: "Daftar PT",
      });
      activityApi.log({ action: "ekspor", target_type: "laporan", target_name: "PDF per PT", group_id: currentGroupId, group_name: groupName });
    } catch { window.alert("Gagal mengekspor batch per PT."); }
    finally { setExporting(false); }
  };

  // Ringkasan UBO (kepemilikan efektif > 25%)
  const uboReport: UBORow[] = useMemo(() => {
    const c = newEffCache(effStep);
    return PERSON_ORDER.map((pid) => {
      const holdings = COMPANY_ORDER
        .map((cid) => ({ cid, frac: effective(pid, cid, c) }))
        .filter((h) => !isZero(h.frac))
        .map((h) => ({ company: COMPANIES[h.cid]?.short ?? h.cid, pct: toPercentString(h.frac), over25: greaterThanPercent(h.frac, 25) }));
      return { name: PERSONS[pid]?.name ?? pid, isUBO: holdings.some((h) => h.over25), holdings };
    }).filter((r) => r.holdings.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupId, effStep, structRev]);

  const onExportUBO = async () => {
    if (exporting) return;
    setExporting(true);
    try { await exportUBOReport(uboReport, groupName); activityApi.log({ action: "ekspor", target_type: "laporan", target_name: "Ringkasan UBO", group_id: currentGroupId, group_name: groupName }); }
    catch { window.alert("Gagal membuat ringkasan UBO."); }
    finally { setExporting(false); }
  };

  const onExportClientSummary = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const companiesRows = COMPANY_ORDER.map((cid) => {
        const c = COMPANIES[cid];
        const lvl = companyFlagLevel(flags, cid);
        const v = activeVersion(cid, effStep);
        const sh = v ? new Set(v.holders.map((h) => h[0])).size : 0;
        const missing = docCompleteness.find((r) => r.cid === cid)?.missing.length ?? 0;
        return { name: c.name, kedudukan: c.kedudukan, status: (lvl ?? "ok") as "ok" | "yellow" | "red", shareholders: sh, docsMissing: missing };
      });
      const completePct = COMPANY_ORDER.length === 0 ? 0 : Math.round(((COMPANY_ORDER.length - ptWithMissing) / COMPANY_ORDER.length) * 100);
      const docTotal = COMPANY_ORDER.reduce((n, cid) => n + COMPANIES[cid].documents.length, 0);
      await exportClientSummary({
        group: groupName,
        dateLabel: active ? ddmmyyyy(active.date) : "-",
        stats: { companies: COMPANY_ORDER.length, documents: docTotal, issues: counts.red + counts.yellow, completePct },
        companies: companiesRows,
        ubo: uboReport.filter((r) => r.isUBO).map((r) => r.name),
      });
      activityApi.log({ action: "ekspor", target_type: "laporan", target_name: "Ringkasan Klien", group_id: currentGroupId, group_name: groupName });
    } catch { window.alert("Gagal membuat ringkasan klien."); }
    finally { setExporting(false); }
  };


  // Kelengkapan dokumen per PT (untuk panel ringkasan grup)
  const docCompleteness = useMemo(() => {
    return COMPANY_ORDER.map((cid) => {
      const c = COMPANIES[cid];
      const items = c.documents.map((d, i) => {
        const dk = `${d.kind}-${i}`;
        const has = !!(d.url || d.storagePath || docExtra[cid]?.[dk]?.url);
        return { label: d.label, kind: d.kind, ada: has };
      });
      return { cid, name: c.name, missing: items.filter((x) => !x.ada) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docExtra, currentGroupId, structRev]);
  const ptWithMissing = docCompleteness.filter((r) => r.missing.length > 0).length;
  const groupName = groupList.find((g) => g.id === currentGroupId)?.name || "Grup";

  // Baris checklist dokumen lengkap (untuk ekspor PDF/CSV)
  const docChecklistRows: DocChecklistRow[] = useMemo(() => {
    return COMPANY_ORDER.map((cid) => {
      const c = COMPANIES[cid];
      return {
        company: c.name,
        items: c.documents.map((d, i) => {
          const dk = `${d.kind}-${i}`;
          const manual = docExtra[cid]?.[dk];
          const url = d.url ?? (manual?.url || "");
          const source = url ? (d.url ? (d.inBundle || "arsip") : "link manual") : "";
          return { label: d.label, status: (url ? "Ada" : "Belum ada") as "Ada" | "Belum ada", source, note: manual?.note || "", url };
        }),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docExtra, currentGroupId, structRev]);
  const [docExporting, setDocExporting] = useState(false);
  const [docExportMissingOnly, setDocExportMissingOnly] = useState(false);
  const rowsForExport = () =>
    docExportMissingOnly
      ? docChecklistRows.filter((r) => r.items.some((it) => it.status === "Belum ada") || r.items.length === 0)
      : docChecklistRows;

  const openExport = (kind: "png" | "pdf") => {
    if (isEmpty || exporting) return;
    // default: tampilkan pemilik yang sedang terlihat di kanvas
    setExportInclude(new Set(owners.map((o) => o.id).filter((id) => !hiddenOwners.has(id))));
    setExportKind(kind);
  };

  // Ekspor PDF multi-halaman: tiap langkah = satu halaman
  const exportAllSteps = async () => {
    if (isEmpty || exporting || STEPS.length === 0) return;
    setExporting(true);
    const prevStep = step;
    const pages: { dataUrl: string; w: number; h: number }[] = [];
    const stepLabels: string[] = [];
    try {
      for (let i = 0; i < STEPS.length; i++) {
        setStep(i);
        await new Promise((r) => setTimeout(r, 160)); // tunggu kanvas render langkah ini
        const s = STEPS[i];
        stepLabels.push(`Langkah ${i + 1}/${STEPS.length}: ${s.label}`);
        const page = await captureScene({
          group: groupName,
          dateLabel: ddmmyyyy(s.date),
          stepLabel: `Langkah ${i + 1}/${STEPS.length}: ${s.label}`,
          hiddenOwners,
          ownerNames,
          categoryLegend,
        });
        pages.push(page);
      }
      await savePagesPDF(pages, groupName, stepLabels);
      activityApi.log({ action: "ekspor", target_type: "laporan", target_name: "PDF Semua Langkah", group_id: currentGroupId, group_name: groupName });
    } catch (e) {
      window.alert("Gagal mengekspor PDF semua langkah.");
    } finally {
      setStep(prevStep);
      setFitSignal((f) => f + 1);
      setExporting(false);
    }
  };

  const confirmExport = async () => {
    const kind = exportKind;
    if (!kind) return;
    setExportKind(null);
    setExporting(true);
    const prevHidden = hiddenOwners;
    const hideForExport = new Set(owners.map((o) => o.id).filter((id) => !exportInclude.has(id)));
    const needTemp = !eqSet(prevHidden, hideForExport);
    try {
      if (needTemp) {
        setHiddenOwners(hideForExport);
        await new Promise((r) => setTimeout(r, 90)); // tunggu kanvas render ulang
      }
      await exportChart(kind, {
        bbox: layout.bbox,
        group: groupList.find((g) => g.id === currentGroupId)?.name || "Grup",
        dateLabel: active ? ddmmyyyy(active.date) : "-",
        hiddenOwners: hideForExport,
        ownerNames,
        categoryLegend,
      });
      activityApi.log({ action: "ekspor", target_type: kind === "pdf" ? "laporan" : "gambar", target_name: kind === "pdf" ? "PDF Struktur" : "PNG Struktur", group_id: currentGroupId, group_name: groupName });
    } catch (e) {
      window.alert("Gagal mengekspor bagan.");
    } finally {
      if (needTemp) setHiddenOwners(prevHidden);
      setExporting(false);
    }
  };

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const goPage = (p: PageId) => {
    setPage(p);
    setFlagOpen(false);
    if (p === "struktur") { setTab("visualisasi"); setFitSignal((f) => f + 1); }
  };
  const goTab = (t: typeof tab) => {
    setTab(t);
    if (t === "visualisasi") setFitSignal((f) => f + 1);
  };
  const focusCompany = (cid: string) => {
    setPage("struktur"); setTab("visualisasi");
    setSelected({ kind: "company", id: cid }); setPinnedId(cid);
    setSearch(""); setFlagOpen(false); setCatHighlight(null);
    setFitSignal((f) => f + 1);
  };
  const onPickHit = (hit: SearchHit) => {
    if (hit.type === "company") { focusCompany(hit.id); }
    else if (hit.type === "person") {
      setPage("struktur"); setTab("visualisasi");
      setSelected({ kind: "person", id: hit.id }); setPinnedId(hit.id);
      setSearch(""); setFlagOpen(false); setFitSignal((f) => f + 1);
    } else { setPage("dokumen"); setSearch(""); }
  };

  const issueCount = counts.red + counts.yellow;

  const ctx: Ctx = {
    groupName, effStep, cache, flags, counts, canEdit, user, theme, onToggleTheme: toggleTheme,
    companyCat, docExtra, focusCompany, onNavigate: goPage,
    onUploadForCompany: (cid: string) => {
      const nm = (groupData[currentGroupId]?.companies?.[cid]?.name) || COMPANIES[cid]?.name || "PT";
      setExtractPreset({ groupId: currentGroupId, companyId: cid, companyName: nm });
      setShowExtract(true);
    },
    onOpenExtract: () => setShowExtract(true),
    openExport, exportAllSteps, exportBatchPerPT, onExportUBO, onExportClientSummary, exporting,
    onOpenUsers: () => setShowUsers(true),
    uboReport, ptWithMissing, docCompleteness,
    groupList, currentGroupId, onChangeGroup: setCurrentGroupId,
  };

  const structureWorkspace = (
    <div className="stk-page" data-testid="page-struktur">
      {/* Action bar */}
      <div className="stk-actionbar">
        <button className="d-btn-primary" onClick={() => setShowExtract(true)} disabled={!canEdit} data-testid="extract-open" title="Buat / tambah struktur dari dokumen (AI)">
          <Upload size={18} /><span className="pt"><b>Ekstraksi AI</b><small>Upload dokumen legalitas</small></span>
        </button>
        {canEdit && <button className="d-btn" onClick={addGroup} data-testid="add-group"><Plus size={16} /> Grup Baru</button>}
        {canEdit && <button className="d-btn" onClick={renameGroupCurrent} disabled={groupList.length === 0} data-testid="rename-group"><Pencil size={15} /> Ubah Nama</button>}
        {canEdit && <button className="d-btn danger" onClick={() => { const g = groupList.find((x) => x.id === currentGroupId); if (g) setConfirmDelete({ id: g.id, name: g.name }); }} disabled={groupList.length === 0} data-testid="delete-group"><Trash2 size={15} /> Hapus</button>}
        {canEdit && <button className="d-btn" onClick={() => setShowShare(true)} disabled={groupList.length === 0 || !groupData[currentGroupId]} data-testid="share-group"><Share2 size={15} /> Bagikan</button>}
        <div className="spacer" />
        <div className="dropdown-wrap">
          <button className="d-btn" onClick={() => setExportMenuOpen((v) => !v)} disabled={isEmpty || exporting} data-testid="export-menu-btn">
            <Download size={15} /> {exporting ? "Memproses…" : "Export"} <ChevronDown size={14} />
          </button>
          {exportMenuOpen && (
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 25 }} onClick={() => setExportMenuOpen(false)} />
              <div className="dropdown-menu" data-testid="export-menu">
                <button className="dropdown-item" onClick={() => { setExportMenuOpen(false); openExport("png"); }} data-testid="export-png"><span className="di-ic"><FileImage size={16} /></span> Download PNG</button>
                <button className="dropdown-item" onClick={() => { setExportMenuOpen(false); openExport("pdf"); }} data-testid="export-pdf"><span className="di-ic"><FileTextIcon size={16} /></span> PDF Struktur Saat Ini</button>
                <button className="dropdown-item" onClick={() => { setExportMenuOpen(false); exportAllSteps(); }} data-testid="export-pdf-all"><span className="di-ic"><FileStack size={16} /></span> PDF Semua Langkah</button>
                <button className="dropdown-item" onClick={() => { setExportMenuOpen(false); exportBatchPerPT(); }} data-testid="export-pdf-per-pt"><span className="di-ic"><Layers size={16} /></span> PDF per PT</button>
                <button className="dropdown-item" onClick={() => { setExportMenuOpen(false); onExportUBO(); }} data-testid="export-ubo"><span className="di-ic"><Users2 size={16} /></span> Ringkasan UBO</button>
                <button className="dropdown-item" onClick={() => { setExportMenuOpen(false); onExportClientSummary(); }} data-testid="export-client"><span className="di-ic"><FileTextIcon size={16} /></span> Ringkasan Klien</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Quick controls */}
      <div className="stk-quickbar">
        <button className={"quick-btn" + (showEffective ? " on" : "")} onClick={() => setShowEffective((v) => !v)} data-testid="toggle-effective"><Percent size={15} /> Efektif</button>
        <button className={"quick-btn" + (legendOpen ? " on" : "")} onClick={() => setLegendOpen((v) => !v)} data-testid="toggle-legend"><Palette size={15} /> Warna</button>
        <button className="quick-btn" onClick={() => setShowDocPanel(true)} disabled={isEmpty} data-testid="toggle-docpanel" title="Kelengkapan berkas legal (Akta, NIB, NPWP) per PT — berbeda dari Data Issues">
          <FileTextIcon size={15} /> Dokumen {ptWithMissing > 0 && <><span className="qdot yellow" /> {ptWithMissing} PT kurang</>}
        </button>
        <div className="spacer" style={{ flex: 1 }} />
      </div>

      {/* Timeline */}
      <div className="stk-tl">
        <div className="stk-tl-date">
          <div className="cal"><Calendar size={19} /></div>
          <div>
            <div className="lbl">Struktur per tanggal</div>
            <div className="big tnum">{active ? ddmmyyyy(active.date) : "—"}</div>
            <div className="sm">Sesuai dengan langkah yang dipilih</div>
          </div>
        </div>
        <div className="stk-tl-mid">
          {STEPS.length === 0 ? (
            <div className="stk-tl-caption"><span>Grup ini belum memiliki data akta. Gunakan Ekstraksi AI untuk mengisi struktur.</span></div>
          ) : (
            <>
              <div className="stk-tl-rail">
                {STEPS.map((s, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div className={"stk-seg" + (i <= effStep ? " done" : "")} />}
                    <button className={"stk-node" + (i < effStep ? " done" : "") + (i === effStep ? " active done" : "")} onClick={() => goStep(i)} data-testid={`step-${i}`} title={s.label}>{i + 1}</button>
                  </React.Fragment>
                ))}
              </div>
              <div className="stk-tl-caption">
                <span className="step-of">Langkah {effStep + 1} dari {STEPS.length}</span>
                <span className="sep">|</span>
                {active && <b>{active.label}.</b>}
                {active && <span className="src">{active.detail}</span>}
              </div>
            </>
          )}
        </div>
        {STEPS.length > 0 && (
          <div className="stk-tl-nav">
            <button className="tl-nav-btn" onClick={() => goStep(effStep - 1)} data-testid="step-prev" disabled={effStep === 0}><ArrowLeft size={18} /></button>
            <button className="tl-nav-btn" onClick={() => goStep(effStep + 1)} data-testid="step-next" disabled={effStep === STEPS.length - 1}><ArrowRight size={18} /></button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="stk-tabs-row">
        {([
          ["visualisasi", "Visualisasi Struktur", NetworkIcon],
          ["perusahaan", "Daftar Perusahaan", Building2Icon],
          ["timeline", "Timeline Perubahan", Clock],
          ["issues", "Data Issues", FlagIcon],
          ["dokumen", "Dokumen", FileTextIcon],
          ["laporan", "Analisis & Laporan", BarChart3],
        ] as const).map(([id, label, Icon]) => (
          <button key={id} className={"stk-tab" + (tab === id ? " active" : "")} onClick={() => goTab(id)} data-testid={`tab-${id}`}>
            <Icon size={15} /> {label}
            {id === "issues" && issueCount > 0 && <span className="tab-badge">{issueCount}</span>}
          </button>
        ))}
        <div className="spacer" />
        <button className="d-btn ghost fullscreen-btn" onClick={() => { setFullscreen((v) => !v); setFitSignal((f) => f + 1); }} data-testid="fullscreen-btn">
          {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />} {fullscreen ? "Keluar" : "Layar Penuh"}
        </button>
      </div>

      {/* Canvas / tab pane */}
      <div className={"stk-canvas-area" + (fullscreen ? " full" : "")}>
        <div className="stk-canvas-box">
          {tab === "visualisasi" ? (
            <>
              {fullscreen && <button className="d-btn ghost fs-exit" onClick={() => { setFullscreen(false); setFitSignal((f) => f + 1); }} data-testid="fullscreen-exit"><Minimize2 size={15} /> Keluar Layar Penuh</button>}
              {isEmpty && (
                <div className="canvas-empty" data-testid="canvas-empty">
                  <div className="ce-illus" aria-hidden="true">
                    <svg width="230" height="170" viewBox="0 0 230 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g className="ce-spark">
                        <path d="M48 66 v14 M41 73 h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                        <path d="M186 52 v12 M180 58 h12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                        <path d="M196 116 v10 M191 121 h10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        <path d="M40 120 v10 M35 125 h10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                      </g>
                      <rect x="66" y="38" width="118" height="80" rx="10" className="ce-win-back" />
                      <rect x="78" y="52" width="118" height="84" rx="11" className="ce-win-front" />
                      <circle cx="90" cy="64" r="2.6" className="ce-dot" />
                      <circle cx="99" cy="64" r="2.6" className="ce-dot" />
                      <circle cx="108" cy="64" r="2.6" className="ce-dot" />
                      <line x1="80" y1="74" x2="194" y2="74" className="ce-line" />
                      <rect x="96" y="98" width="14" height="24" rx="3" className="ce-bar" />
                      <rect x="118" y="88" width="14" height="34" rx="3" className="ce-bar ce-bar-2" />
                      <rect x="140" y="104" width="14" height="18" rx="3" className="ce-bar" />
                      <rect x="162" y="94" width="14" height="28" rx="3" className="ce-bar ce-bar-2" />
                    </svg>
                  </div>
                  <div className="ce-title">Struktur Perusahaan Akan Tampil di Sini</div>
                  <div className="ce-sub">Gunakan dokumen legalitas atau pilih langkah lain untuk melihat visualisasi struktur kepemilikan perusahaan.</div>
                  {canEdit && (
                    <button className="d-btn-primary ce-btn" onClick={() => setShowExtract(true)} data-testid="canvas-empty-extract">
                      <Upload size={18} /><span className="pt"><b>Ekstraksi AI</b><small>Upload dokumen untuk memulai</small></span>
                    </button>
                  )}
                </div>
              )}
              <Canvas
                layout={layout}
                step={effStep}
                catColors={catColors}
                companyCat={companyCat}
                flags={flags}
                selectedId={selected?.id ?? null}
                onSelect={selectEntity}
                onDeselect={() => setSelected(null)}
                search={search}
                showOfficers={showOfficers}
                collapsed={collapsed}
                onToggleCollapse={toggleCollapse}
                uboSet={uboSet}
                fitSignal={fitSignal}
                hiddenOwners={hiddenOwners}
                setHiddenOwners={setHiddenOwners}
                pinnedId={pinnedId}
                setPinnedId={(id) => { setPinnedId(id); if (id) setCatHighlight(null); }}
                onExportChain={onExportChain}
                catHighlight={catHighlight}
                setCatHighlight={setCatHighlight}
              />
              {legendOpen && (
                <div className="legend" data-testid="legend">
                  <h4>Warna kategori</h4>
                  <div className="legend-note">Hanya kategori yang sedang dipakai grup ini. Ganti kategori PT lewat panel detail; pilihan warna &amp; kategori tersimpan di browser.</div>
                  {Object.keys(CATEGORIES).filter((k) => usedCats.has(k)).map((k) => (
                    <div className={"legend-row legend-row-click" + (catHighlight === k ? " active" : "")} key={k} data-testid={`legend-cat-${k}`}
                      onClick={() => { setCatHighlight(catHighlight === k ? null : k); setPinnedId(null); setSelected(null); }}
                      title="Klik untuk menyorot semua PT kategori ini">
                      <input type="color" value={catColors[k] ?? CATEGORIES[k].color} onClick={(e) => e.stopPropagation()} onChange={(e) => setCatColors((p) => ({ ...p, [k]: e.target.value }))} data-testid={`color-${k}`} />
                      <span>{CATEGORIES[k].name}</span>
                    </div>
                  ))}
                  <hr />
                  <label className="legend-check">
                    <input type="checkbox" checked={drawSmall} onChange={(e) => { setDrawSmall(e.target.checked); setFitSignal((f) => f + 1); }} data-testid="chk-small" />
                    <span>Gambar garis untuk kepemilikan di bawah 1%</span>
                  </label>
                  <label className="legend-check">
                    <input type="checkbox" checked={showOfficers} onChange={(e) => { setShowOfficers(e.target.checked); setFitSignal((f) => f + 1); }} data-testid="chk-officers" />
                    <span>Tampilkan pengurus &amp; domisili di kartu</span>
                  </label>
                </div>
              )}
              {selected && (COMPANIES[selected.id] || PERSONS[selected.id]) && (
                <DetailPanel
                  sel={selected}
                  step={effStep}
                  cache={cache}
                  flags={flags}
                  companyCat={companyCat}
                  onChangeCat={(cid, cat) => setCompanyCat((p) => ({ ...p, [cid]: cat }))}
                  companyOps={companyOps}
                  onChangeOps={(cid, val) => setCompanyOps((p) => ({ ...p, [cid]: val }))}
                  primaryKbli={primaryKbli}
                  onChangePrimaryKbli={(cid, code) => {
                    setPrimaryKbli((p) => ({ ...p, [cid]: code }));
                    const suggested = suggestCategoryFromKbli(code);
                    if (suggested) {
                      setCompanyCat((p) => {
                        const cur = p[cid] ?? defaultCat[cid] ?? "none";
                        return cur === "none" ? { ...p, [cid]: suggested } : p;
                      });
                    }
                  }}
                  onClose={() => setSelected(null)}
                  onSelectEntity={selectEntity}
                  docExtra={docExtra}
                  onDocExtra={onDocExtra}
                  canEdit={canEdit}
                  onUploadForCompany={(cid) => {
                    const nm = (groupData[currentGroupId]?.companies?.[cid]?.name) || COMPANIES[cid]?.name || "PT";
                    setExtractPreset({ groupId: currentGroupId, companyId: cid, companyName: nm });
                    setShowExtract(true);
                  }}
                />
              )}
            </>
          ) : (
            <div className="stk-tabpane" style={{ padding: "22px 24px" }} data-testid={`tabpane-${tab}`}>
              {tab === "perusahaan" && <CompaniesPage {...ctx} />}
              {tab === "timeline" && <ChangeTimelinePage {...ctx} />}
              {tab === "issues" && <IssuesPage {...ctx} />}
              {tab === "dokumen" && <DocumentsPage {...ctx} />}
              {tab === "laporan" && <ReportsPage {...ctx} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="dash-root" data-testid="dash-root">
      <Sidebar page={page} onNavigate={goPage} issueCount={issueCount} mini={sidebarMini} onToggleMini={() => setSidebarMini((v) => !v)} isAdmin={user?.role === "admin"} />
      <div className="dash-main">
        <TopHeader
          groupList={groupList}
          currentGroupId={currentGroupId}
          onChangeGroup={setCurrentGroupId}
          search={search}
          setSearch={setSearch}
          onPickHit={onPickHit}
          theme={theme}
          onToggleTheme={toggleTheme}
          notifCount={issueCount}
          onOpenNotif={() => goPage("issues")}
          user={user}
          onProfile={() => goPage("pengaturan")}
          onUsers={() => setShowUsers(true)}
          onSettings={() => goPage("pengaturan")}
          onLogout={logout}
        />
        <div className="dash-body">
          {page === "struktur" ? structureWorkspace : (
            <div className="page-scroll">
              {page === "dashboard" && <DashboardPage {...ctx} />}
              {page === "perusahaan" && <CompaniesPage {...ctx} />}
              {page === "dokumen" && <DocumentsPage {...ctx} />}
              {page === "issues" && <IssuesPage {...ctx} />}
              {page === "laporan" && <ReportsPage {...ctx} />}
              {page === "aktivitas" && user?.role === "admin" && <AuditLogPage {...ctx} />}
              {page === "pengaturan" && <SettingsPage {...ctx} />}
            </div>
          )}
        </div>
      </div>

      {showDocPanel && (
        <div className="modal-overlay" data-testid="doc-panel" onClick={() => setShowDocPanel(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
            <div className="modal-title serif">Kelengkapan dokumen grup</div>
            <div className="modal-sub">Kelengkapan <b>berkas legal</b> per PT (berbeda dari Data Issues yang menilai konsistensi data). Syarat minimal tiap PT: Akta Pendirian, Akta Perubahan (jika ada), NIB, NPWP. Klik nama PT untuk membuka rincian & "Lihat dokumen asli".</div>
            <div className="modal-list">
              {docCompleteness.map((r) => (
                <div className="docsum-row" key={r.cid} data-testid={`docsum-${r.cid}`}>
                  <button className="docsum-name" onClick={() => { focusCompany(r.cid); setShowDocPanel(false); }} data-testid={`docsum-open-${r.cid}`}>{r.name}</button>
                  {r.missing.length === 0
                    ? <span className="doc-badge ok">Lengkap</span>
                    : <span className="doc-badge missing" data-testid={`docsum-missing-${r.cid}`}>Kurang: {r.missing.map((m) => m.label).join(", ")}</span>}
                </div>
              ))}
            </div>
            <div className="modal-actions" style={{ flexWrap: "wrap", gap: 8 }}>
              <label className="legend-check" style={{ marginRight: "auto" }}>
                <input type="checkbox" checked={docExportMissingOnly}
                  onChange={(e) => setDocExportMissingOnly(e.target.checked)} data-testid="doc-export-missing-only" />
                <span>Hanya PT yang dokumennya kurang</span>
              </label>
              <button className="btn" disabled={docExporting}
                onClick={async () => { setDocExporting(true); try { await exportDocChecklistPDF(rowsForExport(), groupName); } catch { window.alert("Gagal membuat PDF."); } finally { setDocExporting(false); } }}
                data-testid="doc-export-pdf" title="Unduh ringkasan kelengkapan dokumen (PDF)">{docExporting ? "…" : "Unduh PDF"}</button>
              <button className="btn"
                onClick={() => exportDocChecklistCSV(rowsForExport(), groupName)}
                data-testid="doc-export-csv" title="Unduh ringkasan kelengkapan dokumen (CSV)">Unduh CSV</button>
              <button className="btn on" onClick={() => setShowDocPanel(false)} data-testid="doc-panel-close">Tutup</button>
            </div>
          </div>
        </div>
      )}

      {exportKind && (
        <div className="modal-overlay" data-testid="export-dialog" onClick={() => setExportKind(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title serif">Ekspor {exportKind.toUpperCase()}</div>
            <div className="modal-sub">Pilih garis kepemilikan (per pemilik) yang ingin ditampilkan pada hasil ekspor.</div>
            <div className="modal-list">
              {owners.map((o) => {
                const on = exportInclude.has(o.id);
                return (
                  <label className={"owner-legend-row" + (on ? "" : " off")} key={o.id} data-testid={`export-owner-${o.id}`}>
                    <input type="checkbox" checked={on} data-testid={`export-toggle-${o.id}`}
                      onChange={() => setExportInclude((prev) => {
                        const n = new Set(prev);
                        if (n.has(o.id)) n.delete(o.id); else n.add(o.id);
                        return n;
                      })} />
                    <span className="owner-swatch" style={{ background: o.color }} />
                    <span className="owner-name">{o.name}</span>
                  </label>
                );
              })}
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setExportInclude(new Set(owners.map((o) => o.id)))} data-testid="export-select-all">Pilih semua</button>
              <button className="btn" onClick={() => setExportInclude(new Set())} data-testid="export-select-none">Kosongkan</button>
              <div style={{ flex: 1 }} />
              <button className="btn" onClick={() => setExportKind(null)} data-testid="export-cancel">Batal</button>
              <button className="btn on" onClick={confirmExport} disabled={exportInclude.size === 0} data-testid="export-confirm">Ekspor</button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="modal-overlay" data-testid="delete-dialog" onClick={() => setConfirmDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
            <div className="modal-title serif">Hapus grup klien?</div>
            <div className="modal-sub">
              Grup <b>{confirmDelete.name}</b> beserta pilihannya (kategori, filter garis, catatan dokumen) akan dihapus. Tindakan ini tidak dapat dibatalkan dan dapat memengaruhi data di dalamnya.
            </div>
            <div className="modal-actions">
              <div style={{ flex: 1 }} />
              <button className="btn" onClick={() => setConfirmDelete(null)} data-testid="delete-cancel">Batal</button>
              <button className="btn danger" onClick={() => doDeleteGroup(confirmDelete.id)} data-testid="delete-confirm">Hapus grup</button>
            </div>
          </div>
        </div>
      )}
      {showUsers && <AdminUsers onClose={() => setShowUsers(false)} />}
      {showShare && (
        <ShareDialog
          gid={currentGroupId}
          groupName={groupList.find((g) => g.id === currentGroupId)?.name ?? "Grup"}
          onClose={() => setShowShare(false)}
        />
      )}
      {showExtract && <ExtractModal onApply={applyExtraction} onClose={() => { setShowExtract(false); setExtractPreset(null); }}
        presetTarget={extractPreset || undefined} onApplyCompany={applyExtractionToCompany}
        existingGroups={groupList.filter((g) => groupData[g.id]).map((g) => ({ id: g.id, name: g.name, companies: Object.values(groupData[g.id].companies || {}).map((c: any) => c.name) }))} />}
    </div>
  );
}
